import toast, { ToastBar, Toaster } from 'react-hot-toast';
import { FaWhatsapp } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import './app.scss';
import AdminRoute from './components/auth/AdminRoute.tsx';
import ProtectedRoute from './components/auth/ProtectedRoute.tsx';
import Button from './components/button/Button.tsx';
import Loading from './components/loading/Loading.tsx';
import ScrollToTop from './components/ScrollToTop.tsx';
import { useLoadingContext } from './login/context/LoadingContext.tsx';
import ForgotPassword from './login/ForgotPassword.tsx';
import LoginScreen from './login/LoginScreen.tsx';
import ResetPassword from './login/ResetPassword.tsx';
import Verify from './login/Verify.tsx';
import OrderConfirmation from './order/OrderConfirmation.tsx';
import Orders from './order/Orders.tsx';
import { useProductContext } from './product/context/ProductContext.tsx';
import PriceList from './product/price-list/PriceList.tsx';
import ProductDetail from './product/product-detail/ProductDetail.tsx';
import ProductForm from './product/product-form/ProductForm.tsx';
import ProductList from './product/product-list/ProductList.tsx';
import ShoppingCart from './shopping-cart/ShoppingCart.tsx';
import UserInfo from './user/UserInfo.tsx';
import { ROUTE_NAME_OPTIONS } from './utils/constants.ts';

const AppContent = () => {
  const location = useLocation();
  const goToWhatsApp = () => {
    window.open('https://wa.me/541124912757', '_blank', 'noopener,noreferrer');
  };

  const { isLoading } = useLoadingContext();
  const { shoppingCart } = useProductContext();

  // Check if current route is an auth page
  const authPages = [
    `/${ROUTE_NAME_OPTIONS.LOGIN}`,
    `/${ROUTE_NAME_OPTIONS.RESET_PASSWORD}`,
    `/${ROUTE_NAME_OPTIONS.FORGOT_PASSWORD}`,
    `/${ROUTE_NAME_OPTIONS.VERIFY}`,
    '/verify',
  ];
  const isAuthPage = authPages.includes(location.pathname);

  const isCartPage =
    location.pathname === `/${ROUTE_NAME_OPTIONS.SHOPPING_CART}`;

  const isHomePage =
    location.pathname === '/' ||
    location.pathname === `/${ROUTE_NAME_OPTIONS.PRODUCTS_LIST}`;

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#ffffff',
            color: '#333333',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            fontSize: '14px',
          },
          success: {
            duration: 2000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#ffffff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#ffffff',
            },
          },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0 2px 0 6px',
                    color: '#aaa',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FiX size={16} />
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
      {isLoading && <Loading />}
      <div className="main-content">
        <div className={isAuthPage ? '' : 'page-container'}>
          <Routes>
            <Route path="/" element={<ProductList />} />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.VERIFY}`}
              element={<Verify />}
            />
            <Route path="/verify" element={<Verify />} />

            <Route
              path={`/${ROUTE_NAME_OPTIONS.PRODUCTS_LIST}`}
              element={<ProductList />}
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.LOGIN}`}
              element={<LoginScreen />}
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.PRODUCTS_LIST}:productBrandId`}
              element={<ProductDetail />}
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.SHOPPING_CART}`}
              element={
                <ProtectedRoute>
                  <ShoppingCart />
                </ProtectedRoute>
              }
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.CREATE_PRODUCT}`}
              element={
                <AdminRoute>
                  <ProductForm />
                </AdminRoute>
              }
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.USER_INFO}`}
              element={
                <ProtectedRoute>
                  <UserInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.ORDERS}`}
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.RESET_PASSWORD}`}
              element={<ResetPassword />}
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.PRICE_LIST}`}
              element={<PriceList />}
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.FORGOT_PASSWORD}`}
              element={<ForgotPassword />}
            />
            <Route
              path={`/${ROUTE_NAME_OPTIONS.ORDER_CONFIRMATION}`}
              element={<OrderConfirmation />}
            />

            <Route path="*" element={<div>Ruta no encontrada</div>} />
          </Routes>
        </div>
      </div>
      <Button
        className={`button whatsapp-icon${isCartPage ? ' whatsapp-hidden-mobile' : ''}${isHomePage && shoppingCart.totalItems > 0 ? ' whatsapp-home-mobile' : ''}`}
        onClick={goToWhatsApp}
      >
        <FaWhatsapp />
      </Button>
    </>
  );
};

const App = () => {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
};

const AppWrapper = () => {
  const location = useLocation();

  // Check if current route is an auth page
  const authPages = [
    `/${ROUTE_NAME_OPTIONS.LOGIN}`,
    `/${ROUTE_NAME_OPTIONS.RESET_PASSWORD}`,
    `/${ROUTE_NAME_OPTIONS.FORGOT_PASSWORD}`,
    `/${ROUTE_NAME_OPTIONS.VERIFY}`,
  ];
  const isAuthPage = authPages.includes(location.pathname);

  return (
    <div className={`main ${isAuthPage ? 'auth-page' : ''}`}>
      <ScrollToTop />
      <AppContent />
    </div>
  );
};

export default App;
